import React from 'react';

const AwningsFeatures: React.FC = () => {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-primary mb-4">Premium Features & Fabrics</h2>
          <p className="text-xl text-gray-600">German engineering meets Canadian durability</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="images/awnings/Colour_samples.jpg" 
              alt="Awning fabric color samples showing various patterns and colors" 
              className="w-full rounded-xl shadow-lg"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-primary mb-6">Premium Fabric Options</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <span className="text-accent mr-3 mt-1">✓</span>
                <div>
                  <h4 className="font-semibold text-primary">Solution-Dyed Acrylic</h4>
                  <p className="text-gray-600">Fade-resistant, water-repellent, 10-year warranty</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-accent mr-3 mt-1">✓</span>
                <div>
                  <h4 className="font-semibold text-primary">UV Protection</h4>
                  <p className="text-gray-600">Blocks up to 98% of harmful UV rays</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-accent mr-3 mt-1">✓</span>
                <div>
                  <h4 className="font-semibold text-primary">Weather Resistant</h4>
                  <p className="text-gray-600">Mold, mildew, and stain resistant</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-accent mr-3 mt-1">✓</span>
                <div>
                  <h4 className="font-semibold text-primary">Easy Maintenance</h4>
                  <p className="text-gray-600">Simple cleaning with soap and water</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Smart Features */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-primary text-center mb-12">Smart Home Integration</h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-4xl mb-4">🗣️</div>
                <h4 className="text-lg font-semibold text-primary mb-2">Voice Control</h4>
                <p className="text-gray-600">"Alexa, extend the awning" - works with all major smart home systems</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-4xl mb-4">📱</div>
                <h4 className="text-lg font-semibold text-primary mb-2">App Control</h4>
                <p className="text-gray-600">Control from anywhere with smartphone app and scheduling features</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-4xl mb-4">🌪️</div>
                <h4 className="text-lg font-semibold text-primary mb-2">Auto Protection</h4>
                <p className="text-gray-600">Wind and rain sensors automatically retract awning for protection</p>
              </div>
            </div>
          </div>
        </div>

        {/* Energy Savings */}
        <div className="mt-16">
          <div className="bg-gradient-to-r from-accent to-green-700 rounded-xl p-8 text-white">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Energy Savings That Pay for Themselves</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  <div className="text-3xl font-bold mb-2">12°C</div>
                  <p className="text-green-100">Cooler Patio Temperature</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">8°C</div>
                  <p className="text-green-100">Cooler Indoor Temperature</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">$200-400</div>
                  <p className="text-green-100">Annual AC Savings</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">5-7</div>
                  <p className="text-green-100">Years to Pay for Itself</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Our Awnings */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-primary text-center mb-12">Why Choose Our Awnings?</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-3xl mb-4">🇩🇪</div>
                <h4 className="text-lg font-semibold text-primary mb-2">German Motors</h4>
                <p className="text-gray-600">Premium Somfy motors with 10+ year lifespan</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-3xl mb-4">⚡</div>
                <h4 className="text-lg font-semibold text-primary mb-2">CSA Electrical</h4>
                <p className="text-gray-600">Certified electrical installation and full insurance</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-3xl mb-4">🛡️</div>
                <h4 className="text-lg font-semibold text-primary mb-2">Long Warranties</h4>
                <p className="text-gray-600">5-10 year comprehensive warranties included</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-3xl mb-4">🏠</div>
                <h4 className="text-lg font-semibold text-primary mb-2">Local Support</h4>
                <p className="text-gray-600">Ontario-based service and support team</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AwningsFeatures;
